import React, { useEffect, useState, useRef } from "react";
import { generate2FA, turnOn2FA, turnOff2FA } from "../services/two_factor.services";

export default function Enable2FA() {
  // Check if user already has 2FA enabled from local storage user state
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const [isAlreadyEnabled, setIsAlreadyEnabled] = useState<boolean>(!!storedUser.isTwoFactorEnabled);

  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [disabling, setDisabling] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Only fetch QR code if 2FA is NOT enabled
    if (!isAlreadyEnabled) {
      handleGenerateQR();
    }
  }, [isAlreadyEnabled]);

  const handleGenerateQR = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await generate2FA();
      setQrCodeUrl(data.qrCodeUrl || data.qrCode || data.url || '');
    } catch (err: any) {
      setError(err?.message || 'Error generating QR code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setOtp(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  // Submit code to turn on 2FA
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fullCode = otp.join('');

    if (fullCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');

    try {
      await turnOn2FA(fullCode);
      setIsAlreadyEnabled(true);

      // Update stored user state
      const updatedUser = { ...storedUser, isTwoFactorEnabled: true };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (err: any) {
      setError(err?.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  // // Turn off 2FA
  // const handleDisable2FA = async () => {
  //   setDisabling(true);
  //   setError('');
  //   try {
  //     await turnOff2FA();
  //     setIsAlreadyEnabled(false);
  //     setOtp(['', '', '', '', '', '']);

  //     // Update stored user state
  //     const updatedUser = { ...storedUser, isTwoFactorEnabled: false };
  //     localStorage.setItem('user', JSON.stringify(updatedUser));
  //   } catch (err: any) {
  //     setError(err?.message || 'Failed to disable 2FA');
  //   } finally {
  //     setDisabling(false);
  //   }
  // };
  // Turn off 2FA
const handleDisable2FA = async () => {
  setDisabling(true);
  setError('');
  try {
    await turnOff2FA();
    
    // 1. Reset OTP input state
    setOtp(['', '', '', '', '', '']);
    
    // 2. Clear old QR code state so it doesn't flash old data
    setQrCodeUrl('');

    // 3. Update localStorage
    const updatedUser = { ...storedUser, isTwoFactorEnabled: false };
    localStorage.setItem('user', JSON.stringify(updatedUser));

    // 4. Update UI view state (triggers useEffect to generate a new fresh QR code)
    setIsAlreadyEnabled(false);
  } catch (err: any) {
    setError(err?.message || 'Failed to disable 2FA');
  } finally {
    setDisabling(false);
  }
};

  // --------------------------------------------------------------------------
  // STATE 1: 2FA is already enabled for this user
  // --------------------------------------------------------------------------
  if (isAlreadyEnabled) {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-xl shadow-md text-center border border-gray-100">
        <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-1">Two-Factor Authentication Active</h2>
        <p className="text-sm text-gray-500 mb-6">
          Your account is secured with 2FA using your authenticator app.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleDisable2FA}
          disabled={disabling}
          className="w-full py-2.5 px-4 bg-red-50 hover:bg-red-100 text-red-600 font-medium rounded-lg border border-red-200 transition-colors duration-150 disabled:opacity-50"
        >
          {disabling ? 'Disabling...' : 'Disable Two-Factor Authentication'}
        </button>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // STATE 2: Setup 2FA Flow (QR code + OTP code verification)
  // --------------------------------------------------------------------------
  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-xl shadow-md border border-gray-100">
      <h2 className="text-xl font-bold text-gray-900 mb-1">Set Up Two-Factor Authentication</h2>
      <p className="text-sm text-gray-500 mb-6">
        Scan the QR code below using your authenticator app (Authenticator appအား playstore တွင်ရယူပါ).
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* QR Code */}
      <div className="flex justify-center items-center my-6 min-h-[160px] bg-gray-50 rounded-lg p-4 border border-dashed border-gray-200">
        {loading ? (
          <p className="text-sm text-gray-400 animate-pulse">Generating QR Code...</p>
        ) : qrCodeUrl ? (
          <img src={qrCodeUrl} alt="2FA QR Code" className="w-44 h-44 object-contain" />
        ) : (
          <button
            type="button"
            onClick={handleGenerateQR}
            className="text-sm text-indigo-600 hover:underline font-medium"
          >
            Retry Loading QR
          </button>
        )}
      </div>

      {/* OTP Code Input */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 text-center">
            Enter 6-Digit Verification Code
          </label>
          <div className="flex justify-between gap-2" onPaste={handlePaste}>
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={(el) => { inputRefs.current[idx] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                className="w-11 h-12 text-center text-lg font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
              />
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={verifying || otp.join('').length !== 6}
          className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium rounded-lg shadow transition-colors duration-150"
        >
          {verifying ? 'Verifying...' : 'Enable 2FA'}
        </button>
      </form>
    </div>
  );
}